/**
 * Environment-aware Logger
 *
 * Provides logging that is:
 * - Active in development for debugging
 * - Silent in production to prevent data leakage
 * - Can be selectively enabled in production via localStorage for debugging
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.debug('Order submitted', { orderId });
 *   logger.info('Connection established');
 *   logger.warn('Degraded mode');
 *   logger.error('Failed to fetch', error);
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

function getConfig(): LoggerConfig {
  const isProduction = process.env.NODE_ENV === 'production';
  const isTest = process.env.NODE_ENV === 'test';

  // In test environment, only show errors to reduce noise
  if (isTest) {
    return { enabled: true, minLevel: 'error' };
  }

  // In production, logging is disabled by default
  // Can be enabled via localStorage for debugging: localStorage.setItem('debug', 'true')
  if (isProduction) {
    try {
      const debugEnabled = localStorage.getItem('debug') === 'true';
      return { enabled: debugEnabled, minLevel: 'warn' };
    } catch {
      return { enabled: false, minLevel: 'error' };
    }
  }

  // In development, show all logs
  return { enabled: true, minLevel: 'debug' };
}

function shouldLog(level: LogLevel): boolean {
  const config = getConfig();
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

function formatMessage(level: LogLevel, message: string): string {
  const timestamp = new Date().toISOString();
  return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
}

export const logger = {
  debug: (message: string, ...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.debug(formatMessage('debug', message), ...args);
    }
  },

  info: (message: string, ...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message), ...args);
    }
  },

  warn: (message: string, ...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', message), ...args);
    }
  },

  error: (message: string, ...args: unknown[]): void => {
    if (shouldLog('error')) {
      console.error(formatMessage('error', message), ...args);
    }
  }
};

/**
 * No-op function for stripping debug statements in production
 * Use this when you want a debug statement that's completely removed in production
 */
export const debugOnly = process.env.NODE_ENV === 'production'
  ? (): void => {}
  : (fn: () => void): void => fn();
